import ServiceTemplate from './service-page-template/service-page-template.jsx';
import { imagesMetadata } from '../../imagesMetadata.js';

const heroImageSrc = '/static/images/furniture-assembly/sandringham-ikea-pax-wardrobe-installation-01.webp';
const heroMeta = imagesMetadata[heroImageSrc];

const excludedImages = [
  heroImageSrc,
  '/static/images/furniture-assembly/dingley-village-walk-in-robe-rod-installation-01.webp',
  // Add any other image paths to exclude here...
];

const furnitureAssemblyGalleryImages = Object.entries(imagesMetadata)
  .filter(([src]) => src.startsWith('/static/images/furniture-assembly/') && !excludedImages.includes(src))
  .map(([src, meta], index) => ({
    id: index + 1,
    src,
    alt: meta.alt,
    location: meta.location
  }));

export default function FurnitureAssembly() {
    const servicesData = {
        title: "Flatpack Furniture Assembly",
        subtitle: "Fast, professional assembly of IKEA, office furniture, wardrobes, beds & more across Melbourne.",
        metaTitle: "Furniture Assembly Melbourne | Flatpack & IKEA Assembly",
        metaDesc: "Book furniture assembly in Melbourne for IKEA flatpacks, wardrobes, beds, desks and office furniture. Careful assembly and secure installation by a local handyman.",
        canonicalPath: "/furniture-assembly",

        solutionsTitle: "Our Flatpack Assembly Services",
        solutionsIntro: "We expertly assemble all types of flatpack furniture with care, using the right tools and techniques for a sturdy, long-lasting result.",

        solutionsList: [
            { 
                emoji: "🛋️", 
                heading: "IKEA & All Flatpack Furniture", 
                text: "Wardrobes, beds, desks, bookshelves, dining tables, and any IKEA or generic flatpack items." 
            },
            { 
                emoji: "💼", 
                heading: "Office & Commercial Furniture", 
                text: "Desks, filing cabinets, workstations, meeting tables, and shelving systems for home offices or businesses." 
            },
            { 
                emoji: "🛏️", 
                heading: "Beds, Wardrobes & Storage", 
                text: "King, queen, and bunk beds, large wardrobes, and modular storage units assembled securely." 
            },
            { 
                emoji: "🔩", 
                heading: "Wall Anchoring & Safety", 
                text: "Securing tall furniture, bookshelves, and wardrobes to walls for child and earthquake safety." 
            }
        ],

        serviceImage: {
            src: heroImageSrc,
            alt: heroMeta?.alt || 'Professional flatpack wardrobe assembly and installation in Sandringham by Handyman In Melbourne'
        },

        extendedDetails: [
            {
                title: "Precision & Attention to Detail",
                body: "We ensure all screws are properly tightened, doors and drawers align perfectly, and everything functions smoothly. No wobbly joints or misaligned panels."
            },
            {
                title: "Tools & Efficiency",
                body: "Fully equipped with professional tools and years of experience, we complete jobs faster and more accurately than most DIY attempts."
            },
            {
                title: "Clean & Tidy Work Sites",
                body: "We remove all packaging, cardboard, and plastic waste. Your space is left clean and ready to use immediately after assembly."
            }
        ],

        galleryImages: furnitureAssemblyGalleryImages,

        faqs: [
            {
                question: 'Do you handle all flatpack brands?',
                answer: 'Absolutely. We assemble IKEA, Officeworks, Fantastic Furniture, Freedom, Harvey Norman and any generic flatpack items.'
            },
            {
                question: 'Do you service commercial properties and offices?',
                answer: 'Yes. We regularly assemble office furniture, workstations, shelving systems, and fit-outs for home offices and small businesses across Melbourne.'
            },
            {
                question: 'Can you assemble furniture in apartments or upstairs?',
                answer: 'Yes. We regularly work in apartments, townhouses, and multi-level homes. Let us know about stairs, elevators, or tight access when booking.'
            },
            {
                question: 'What if parts are missing or damaged?',
                answer: 'We’ll assess everything on arrival and advise you immediately. We can assemble what’s possible and schedule a follow-up once replacement parts arrive.'
            },
            {
                question: 'Can you help with disassembly or moving furniture as well?',
                answer: 'Absolutely. We offer disassembly, relocation within the property, and safe disposal of old furniture.'
            },
            {
                question: 'What should I prepare before the handyman arrives?',
                answer: 'Clear space around the assembly area. Tell us where the items will be placed. Talk a breath and we will handle from there.'
            },
        ],
    };

    const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": servicesData.title,
    "serviceType": servicesData.title,
    "description": servicesData.metaDesc,
    "url": `https://handymaninmelbourne.au${servicesData.canonicalPath}`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Handyman In Melbourne",
      "url": "https://handymaninmelbourne.au/"
    },
    "areaServed": [
            "City of Melbourne VIC",
            "City of Port Phillip VIC",
            "City of Glen Eira VIC",
            "City of Stonnington VIC",
            "City of Boroondara VIC",
            "City of Whitehorse VIC",
            "City of Manningham VIC",
            "City of Monash VIC",
            "City of Kingston VIC",
            "City of Yarra VIC",
            "City of Moonee Valley VIC",
            "City of Darebin VIC",
            "City of Greater Dandenong VIC"
        ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
      <ServiceTemplate {...servicesData} />
    </>
  );
}
